import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, CalendarCheck, Info, X } from 'lucide-react'
import type { Booking, Child, Event, SocialSecurityFund } from '../api/client'
import { createBooking, fetchBookings, fetchChildren, fetchSocialSecurityFunds, fetchUpcomingEvents } from '../api/client'
import { BookingInfoSection } from '../components/BookingInfoSection'
// NavigationBar removed - provided by DashboardLayout
import { SocialSecurityFundSelect, SocialSecuritySubFundSelect } from '../components/SocialSecuritySelect'

export function AddBookingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(['bookings', 'common'])
  
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [friendError, setFriendError] = useState<string | null>(null)
  const [socialSecurityFundError, setSocialSecurityFundError] = useState<string | null>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [socialSecurityFunds, setSocialSecurityFunds] = useState<SocialSecurityFund[]>([])
  
  // Form fields
  const [selectedChild, setSelectedChild] = useState<number | ''>('')
  const [selectedEvent, setSelectedEvent] = useState<number | ''>('')
  const [friendRegistrationNumbers, setFriendRegistrationNumbers] = useState<string[]>([])
  const [friendRegistrationInput, setFriendRegistrationInput] = useState<string>('')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const [selectedSocialSecurityFund, setSelectedSocialSecurityFund] = useState<number | null>(null)
  const [selectedSocialSecuritySubFund, setSelectedSocialSecuritySubFund] = useState<number | null>(null)
  const [dypaProtocolNumber, setDypaProtocolNumber] = useState<string>('')
  const [dypaProtocolError, setDypaProtocolError] = useState<string | null>(null)
  const [efkaVoucherNumber, setEfkaVoucherNumber] = useState<string>('')
  const [efkaVoucherError, setEfkaVoucherError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        // Fetch children, events, bookings, and social security funds data in parallel
        const [childrenData, eventsData, bookingsData, fundsData] = await Promise.all([
          fetchChildren(),
          fetchUpcomingEvents(),
          fetchBookings(),
          fetchSocialSecurityFunds()
        ])
        
        setChildren(childrenData || [])
        setEvents(eventsData || [])
        setBookings(bookingsData || [])
        setSocialSecurityFunds(fundsData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [t])
  
  // Reset sub-fund when fund changes
  useEffect(() => {
    setSelectedSocialSecuritySubFund(null)
  }, [selectedSocialSecurityFund])
  
  // Get the selected fund object
  const selectedFund = socialSecurityFunds.find(fund => fund.id === selectedSocialSecurityFund)
  
  // Get the sub-funds for the selected fund
  const subFunds = selectedFund?.sub_funds || []
  
  // Check if the selected fund has sub-funds
  const hasSocialSecuritySubFunds = selectedFund && selectedFund.sub_funds.length > 0
  
  // Filter events based on the selected child
  const filteredEvents = useMemo(() => {
    if (!selectedChild) return events;
    
    // Get the event IDs for which the selected child already has a booking
    const childBookingEventIds = bookings
      .filter(booking => booking.child === selectedChild && booking.status !== 'cancelled')
      .map(booking => booking.event);
    
    // Return only events for which the child doesn't have a booking
    return events.filter(event => !childBookingEventIds.includes(event.id));
  }, [events, selectedChild, bookings]);
  
  // Validate DYPA protocol number format: XX/YYYY/XXXXXXXXXXXX where YYYY must be the current year
  const validateDypaProtocolNumber = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null }; // Empty is allowed
    
    const pattern = /^(\d{2})\/(\d{4})\/(\d{12})$/;
    const match = value.match(pattern);
    
    if (!match) {
      return {
        isValid: false,
        error: t('errors.invalidDypaFormat', { 
          ns: 'bookings', 
          defaultValue: 'DYPA protocol number must be in format: XX/YYYY/XXXXXXXXXXXX' 
        })
      };
    }
    
    // Extract the year from the protocol number
    const year = parseInt(match[2], 10);
    const currentYear = new Date().getFullYear();
    
    if (year !== currentYear) {
      return {
        isValid: false,
        error: t('errors.invalidDypaYear', { 
          ns: 'bookings', 
          defaultValue: `DYPA protocol year must be ${currentYear}`,
          year: currentYear
        })
      };
    }
    
    return { isValid: true, error: null };
  };
  
  const handleDypaProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDypaProtocolNumber(value);
    // Clear error when user types
    setDypaProtocolError(null);
  };

  // Format an EFKA voucher to XXXX-XXXX-XXXX-XXXX (16 digits, dash every 4)
  const formatEfkaVoucher = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1-').replace(/-$/, '');
  };

  // Validate EFKA voucher: exactly 16 digits (empty is allowed)
  const validateEfkaVoucher = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null };

    if (value.replace(/\D/g, '').length !== 16) {
      return {
        isValid: false,
        error: t('errors.invalidEfkaFormat', {
          ns: 'bookings',
          defaultValue: 'EFKA voucher number must be in format: XXXX-XXXX-XXXX-XXXX'
        })
      };
    }

    return { isValid: true, error: null };
  };

  // Auto-insert dashes as the user types, preserving the cursor position
  const handleEfkaVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const previousValue = input.value;
    const digitsBeforeCursor = previousValue
      .slice(0, input.selectionStart ?? previousValue.length)
      .replace(/\D/g, '').length;

    const formatted = formatEfkaVoucher(previousValue);
    setEfkaVoucherNumber(formatted);
    setEfkaVoucherError(null);

    // Restore the cursor after the same number of digits in the formatted value
    requestAnimationFrame(() => {
      let pos = 0;
      let seen = 0;
      while (pos < formatted.length && seen < digitsBeforeCursor) {
        if (/\d/.test(formatted[pos])) seen++;
        pos++;
      }
      input.setSelectionRange(pos, pos);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!selectedChild || !selectedEvent) {
      setError(t('errors.requiredFields', { ns: 'bookings' }))
      return
    }
    
    // Validate terms acceptance
    if (!termsAccepted) {
      setError(t('errors.termsRequired', { ns: 'bookings' }))
      return
    }
    
    // Validate social security fund and sub-fund
    if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund) {
      // Don't set the top error message, as we're showing it under the field
      return
    }
    
    // Validate DYPA protocol number if ΔΥΠΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && dypaProtocolNumber) {
      const { isValid, error } = validateDypaProtocolNumber(dypaProtocolNumber);
      if (!isValid) {
        setDypaProtocolError(error);
        return;
      }
    }

    // Validate EFKA voucher number if ΕΦΚΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ' && efkaVoucherNumber) {
      const { isValid, error } = validateEfkaVoucher(efkaVoucherNumber);
      if (!isValid) {
        setEfkaVoucherError(error);
        return;
      }
    }

    setSubmitting(true)
    setError(null)
    setFriendError(null)
    setSocialSecurityFundError(null)
    
    try {
      // Set dypaProtocolNumber to empty string if the selected fund is not ΔΥΠΑ
      const finalDypaProtocolNumber =
        selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ'
          ? dypaProtocolNumber
          : '';

      // Set efkaVoucherNumber to empty string if the selected fund is not ΕΦΚΑ
      const finalEfkaVoucherNumber =
        selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ'
          ? efkaVoucherNumber
          : '';

      // Create the base data object
      const baseData = {
        child: selectedChild,
        event: selectedEvent,
        friend_registration_numbers: friendRegistrationNumbers,
        social_security_fund_id: selectedSocialSecurityFund || undefined,
        dypa_protocol_number: finalDypaProtocolNumber,
        efka_voucher_number: finalEfkaVoucherNumber,
        status: 'interested' as const
      };
      
      // Create the final data object with or without the sub-fund
      let bookingData;
      if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && selectedSocialSecuritySubFund) {
        // Include the sub-fund if it's selected and valid
        bookingData = {
          ...baseData,
          social_security_sub_fund_id: selectedSocialSecuritySubFund
        };
      } else {
        // Explicitly set sub-fund to undefined instead of null
        bookingData = {
          ...baseData,
          social_security_sub_fund_id: undefined
        };
      }
      
      await createBooking(bookingData)
      navigate('/') // Go back to home page after successful creation
    } catch (err: unknown) {
      // Check for specific error messages from the API
      const error = err as any; // Type assertion for backward compatibility
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for friend-related errors
        if (errorData.friend_registration_numbers) {
          const friendError = Array.isArray(errorData.friend_registration_numbers) 
            ? errorData.friend_registration_numbers[0] 
            : errorData.friend_registration_numbers;
          
          // Check for gender validation error
          if (friendError.includes('same gender')) {
            setFriendError(t('errors.friendGenderMismatch', { ns: 'bookings' }))
          } 
          // Check for invalid registration numbers
          else if (friendError.includes('Invalid registration numbers')) {
            setFriendError(t('errors.invalidRegistrationNumbers', { ns: 'bookings' }))
          }
          // Check for age difference error
          else if (friendError.includes('within 1.5 years of age difference')) {
            setFriendError(t('errors.ageDifference', { ns: 'bookings' }))
          }
          else if (friendError.includes('resulting group would include children with an age difference')) {
            setFriendError(t('errors.groupAgeDifference', { ns: 'bookings' }))
          }
          // Check for no booking error
          else if (friendError.includes('must have their own booking for the same period')) {
            setFriendError(t('errors.noBooking', { ns: 'bookings' }))
          }
          // Check for cancelled booking error
          else if (friendError.includes('must have an active booking')) {
            setFriendError(t('errors.cancelledBooking', { ns: 'bookings' }))
          }
          // Other friend-related errors
          else {
            setFriendError(friendError)
          }
        } 
        // Check for social security fund errors
        else if (errorData.social_security_fund_id) {
          const fundError = Array.isArray(errorData.social_security_fund_id)
            ? errorData.social_security_fund_id[0]
            : errorData.social_security_fund_id;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        // Check for social security fund errors from the model validation
        else if (errorData.social_security_fund) {
          const fundError = Array.isArray(errorData.social_security_fund)
            ? errorData.social_security_fund[0]
            : errorData.social_security_fund;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        // Check for non_field_errors (model-level validation)
        else if (errorData.non_field_errors) {
          const nonFieldError = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors[0]
            : errorData.non_field_errors;

          if (nonFieldError.includes('16 years old or younger')) {
            setError(t('errors.childTooOld', { ns: 'bookings' }))
          } else {
            setError(nonFieldError)
          }
        }
        else {
          setError(t('errors.createFailed', { ns: 'bookings' }))
        }
      } else {
        setError(t('errors.createFailed', { ns: 'bookings' }))
      }
    } finally {
      setSubmitting(false)
    }
  }
  
  // Validate registration number format: TR-{year}-{5 digits}
  const isValidRegistrationNumber = (regNumber: string): boolean => {
    const regNumberPattern = /^TR-\d{4}-\d{5}$/;
    return regNumberPattern.test(regNumber);
  }

  const addFriendRegistration = () => {
    if (!friendRegistrationInput.trim()) return
    
    // Check if already in the list
    if (friendRegistrationNumbers.includes(friendRegistrationInput.trim())) {
      setFriendError(t('errors.duplicateFriend', { ns: 'bookings' }))
      return
    }
    
    // Validate registration number format
    const regNumber = friendRegistrationInput.trim()
    if (!isValidRegistrationNumber(regNumber)) {
      setFriendError(t('errors.invalidRegistrationFormat', { ns: 'bookings' }))
      return
    }
    
    // Check if the registration number belongs to the selected child
    const selectedChildObj = children.find(child => child.id === selectedChild)
    if (selectedChildObj && selectedChildObj.registration_number === regNumber) {
      setFriendError(t('errors.cannotAddSelf', { ns: 'bookings', defaultValue: "You cannot add the child as their own friend" }))
      return
    }
    
    // Clear any friend errors when adding a new friend
    setFriendError(null)
    setFriendRegistrationNumbers([...friendRegistrationNumbers, regNumber])
    setFriendRegistrationInput('')
  }
  
  const removeFriendRegistration = (index: number) => {
    // Clear any friend errors when removing a friend
    setFriendError(null)
    
    const newList = [...friendRegistrationNumbers]
    newList.splice(index, 1)
    setFriendRegistrationNumbers(newList)
  }
  
  return (
    <div>
      
      {loading ? (
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        </div>
      ) : children.length === 0 ? (
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 p-8">
            <div className="text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-foreground mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('bookingsBox.noChildren.title', { ns: 'bookings' })}
              </h2>
              <p className="mb-6">
                {t('bookingsBox.noChildren.message', { ns: 'bookings' })}
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2">
                  {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
                </Link>
                <Link to="/children/add" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50">
                  {t('bookingsBox.noChildren.addChildButton', { ns: 'bookings' })}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <main className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6">
            <div className="flex items-center mb-6">
              <CalendarCheck className="h-6 w-6 mr-2 text-foreground" />
              <h1 className="text-2xl font-semibold text-foreground">
                {t('bookingsBox.addBooking')}
              </h1>
            </div>
            
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 mb-4">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex items-start gap-2 p-2 bg-muted rounded-md text-xs text-muted-foreground mb-4">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{t('bookingNote')}</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Child Selection */}
              <div>
                <label htmlFor="child" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.child')} *
                </label>
                <select
                  id="child"
                  value={selectedChild}
                  onChange={(e) => setSelectedChild(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  required
                >
                  <option value="">{t('bookingsBox.selectChild')}</option>
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {`${child.first_name} ${child.last_name}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Event Selection */}
              <div>
                <label htmlFor="event" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.event')} *
                </label>
                <select
                  id="event"
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  required
                >
                  <option value="">{t('bookingsBox.selectEvent')}</option>
                  {filteredEvents.map((event) => {
                    const startDate = new Date(event.start_date);
                    const endDate = new Date(event.end_date);
                    
                    // Format as dd/mm/yyyy
                    const formatDate = (date: Date) => {
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = (date.getMonth() + 1).toString().padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    };
                    
                    // Get waiting list text based on current language
                    const waitingListText = t('waitingList', { ns: 'upcomingPeriods', defaultValue: 'waiting list' });
                    
                    return (
                      <option key={event.id} value={event.id}>
                        {event.name} ({formatDate(startDate)} - {formatDate(endDate)})
                        {event.is_full ? ` - ${waitingListText}` : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedChild && events.length > 0 && filteredEvents.length === 0 && (
                  <p className="text-xs text-destructive mt-1">
                    {t('errors.noAvailableEvents', { ns: 'bookings', defaultValue: 'This child already has bookings for all available events' })}
                  </p>
                )}
              </div>
              
              {/* Social Security Fund Selection */}
              <div>
                <label htmlFor="social-security-fund" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.socialSecurityRegistry')} *
                </label>
                <SocialSecurityFundSelect
                  funds={socialSecurityFunds}
                  value={selectedSocialSecurityFund}
                  onChange={(value) => {
                    setSelectedSocialSecurityFund(value);
                    setSocialSecurityFundError(null);
                  }}
                  placeholder={t('bookingsBox.selectSocialSecurityRegistry')}
                  className={`w-full ${socialSecurityFundError ? 'border-2 border-destructive' : ''}`}
                />
                {socialSecurityFundError && (
                  <p className="text-xs text-destructive mt-1">
                    {socialSecurityFundError}
                  </p>
                )}
              </div>
              
              {/* Social Security Sub Fund Selection - Only show if the selected fund has sub-funds */}
              {selectedSocialSecurityFund && hasSocialSecuritySubFunds && (
                <div>
                  <label htmlFor="social-security-sub-fund" className="block text-sm font-medium mb-1.5">
                    {t('bookingsBox.socialSecurityFund')} {hasSocialSecuritySubFunds && '*'}
                  </label>
                  <SocialSecuritySubFundSelect
                    subFunds={subFunds}
                    value={selectedSocialSecuritySubFund}
                    onChange={setSelectedSocialSecuritySubFund}
                    placeholder={t('bookingsBox.selectSocialSecurityFund')}
                    className="w-full"
                    isDisabled={!selectedSocialSecurityFund || !hasSocialSecuritySubFunds}
                  />
                  {selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund && (
                    <p className="text-xs text-destructive mt-1">
                      {t('errors.socialSecurityFundRequired', { ns: 'bookings', defaultValue: 'Πρέπει να επιλέξετε Ταμείο Φορέα για αυτόν τον Φορέα Ασφάλισης' })}
                    </p>
                  )}
                </div>
              )}
              
              {/* DYPA Protocol Number */}
              {selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && (
                <div>
                  <label htmlFor="dypa-protocol-number" className="block text-sm font-medium mb-1.5">
                    {t('bookingsBox.dypaProtocolNumber')}
                  </label>
                  <input
                    type="text"
                    id="dypa-protocol-number"
                    value={dypaProtocolNumber}
                    onChange={handleDypaProtocolChange}
                    className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${dypaProtocolError ? 'border-destructive' : 'border-input'}`}
                    placeholder={`XX/${new Date().getFullYear()}/XXXXXXXXXXXX`}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('bookingsBox.dypaProtocolFormat', { ns: 'bookings' })}: XX/{new Date().getFullYear()}/XXXXXXXXXXXX
                  </p>
                  {dypaProtocolError && (
                    <p className="text-xs text-destructive mt-1">{dypaProtocolError}</p>
                  )}
                </div>
              )}

              {/* EFKA Voucher Number */}
              {selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ' && (
                <div>
                  <label htmlFor="efka-voucher-number" className="block text-sm font-medium mb-1.5">
                    {t('bookingsBox.efkaVoucherNumber')}
                  </label>
                  <input
                    type="text"
                    id="efka-voucher-number"
                    value={efkaVoucherNumber}
                    onChange={handleEfkaVoucherChange}
                    className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${efkaVoucherError ? 'border-destructive' : 'border-input'}`}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    inputMode="numeric"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('bookingsBox.efkaVoucherFormat', { ns: 'bookings' })}: XXXX-XXXX-XXXX-XXXX
                  </p>
                  {efkaVoucherError && (
                    <p className="text-xs text-destructive mt-1">{efkaVoucherError}</p>
                  )}
                </div>
              )}

              {/* Friends */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.friends')}
                </label>
                
                <div className="mt-2 flex">
                  <input
                    type="text"
                    value={friendRegistrationInput}
                    onChange={(e) => {
                      // Clear friend error when user types
                      if (friendError) setFriendError(null);
                      setFriendRegistrationInput(e.target.value);
                    }}
                    placeholder={t('bookingsBox.friendRegistrationPlaceholder')}
                    className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${friendError ? 'border-destructive' : 'border-input'}`}
                  />
                  <button
                    type="button"
                    onClick={addFriendRegistration}
                    className="ml-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {t('bookingsBox.add')}
                  </button>
                </div>
                
                <p className="text-xs text-muted-foreground mt-1">
                  {t('bookingsBox.registrationNumberFormat')}
                </p>
                
                {friendError && (
                  <p className="text-xs text-destructive mt-1">{friendError}</p>
                )}
                
                {friendRegistrationNumbers.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      {t('bookingsBox.addedFriends')}:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {friendRegistrationNumbers.map((regNum, index) => (
                        <div 
                          key={index} 
                          className="flex items-center bg-muted rounded-full px-3 py-1 border border-border"
                        >
                          <span className="text-sm font-medium mr-2">{regNum}</span>
                          <button
                            type="button"
                            onClick={() => removeFriendRegistration(index)}
                            className="text-muted-foreground hover:text-destructive text-xs focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fund-specific Information Section */}
              {selectedSocialSecurityFund && (
                <BookingInfoSection fundName={selectedFund?.name} />
              )}
              
              {/* Terms and Conditions */}
              <div className="flex items-start mt-4">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    required
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="font-medium">
                    {t('bookingsBox.termsAgreement')} *
                  </label>
                  <p className="text-muted-foreground">
                    {t('bookingsBox.termsDescription')} <a href="https://ranch.gr/el/kanonismos" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('bookingsBox.termsLink')}</a>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-row gap-4 pt-4">
                <Link to="/" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 w-1/2 text-center">
                  {t('bookingsBox.cancel')}
                </Link>
                
                <button
                  type="submit"
                  disabled={submitting || !termsAccepted}
                  className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors shadow h-9 px-4 py-2 w-1/2 ${termsAccepted ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-gray-400 cursor-not-allowed'}`}
                >
                  {submitting ? t('common.submitting') : t('bookingsBox.createBooking')}
                </button>
              </div>
            </form>
          </div>
        </main>
      )}
    </div>
  )
} 